import {
  useWSModalCustom,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  BatchCreate,
  EngagementStatus,
  PayeeImportBatchCreate,
  PayeeImportBatchCreateType
} from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches, queriesEngagements } from "@query";

import { ErrorAlert } from "@components/ErrorAlert";
import { IdempotencyKey } from "../../../../v3/utils/idempotencyKey";
import { selectorEngagementOptions } from "../../../EngagementTypes/selectors/selectorEngagementOptions";

type FormData = {
  name: string;
  notes: string;
  engagementId: string;
};

type Props = WSModalCustomProps<string | null>;

const ENGAGEMENT_TOOLTIP = `An engagement is a type of work arrangement that allows payers to organize their diverse work relationships in a flexible way. One engagement can be assigned when adding a new contractor. Other engagements can be added later.

    If you have not set up engagements or do not plan to use engagements, you can skip this field. Engagements can be updated in the Contractors section of the app at any time.`;

/**
 * Name, notes and engagement of a new contractor import batch.
 *
 * v1 collected these after creating the batch and saved them with a batch
 * update. v3 accepts `_configuration.engagementId` at create time only, so the
 * batch is created when this form is submitted rather than before it opens.
 */
const ModalNewContractorBatch: React.FC<Props> = props => {
  const [createBatch, createBatchMeta] =
    queriesBatches.createBatch.useMutation();

  const queryEngagements = queriesEngagements.listEngagements.useQueryAll(
    { filter: { status: { anyOf: [EngagementStatus.ACTIVE] } } },
    { refetchOnMount: false, retry: false }
  );
  const engagementOptions = selectorEngagementOptions(
    (queryEngagements.flatData || []).filter(
      engagement => !engagement.isDefault
    )
  );

  const handleSubmit = async (data: FormData) => {
    const name = data.name.trim();
    const notes = data.notes.trim();
    const engagementId = data.engagementId;

    const idempotencyKey = new IdempotencyKey({
      type: PayeeImportBatchCreateType.PAYEE_IMPORT,
      name,
      notes,
      engagementId
    });

    // The SDK generates `BatchCreate` as `{ type: 'PayeeImport' } &
    // PayeeImportBatchCreate | …`, and intersecting the bare literal with the
    // nominal `PayeeImportBatchCreateType` member collapses every property to
    // `never` — so no value satisfies it. Build the real variant type and cast
    // once here, at the SDK boundary.
    const batchCreate: PayeeImportBatchCreate = {
      type: PayeeImportBatchCreateType.PAYEE_IMPORT,
      ...(engagementId ? { _configuration: { engagementId } } : {}),
      ...(name ? { name } : {}),
      ...(notes ? { notes } : {})
    };

    await createBatch(
      {
        idempotencyKey: idempotencyKey.value,
        batchCreate: batchCreate as BatchCreate
      },
      {
        onSuccess: batch => {
          idempotencyKey.destroy();
          props.onClose(batch.id ?? null);
        }
      }
    );
  };

  return (
    <WSForm<FormData>
      defaultValues={{ name: "", notes: "", engagementId: "" }}
      onSubmit={handleSubmit}
    >
      <WSModal
        title="New batch"
        size="S"
        onClose={() => props.onClose(null)}
        actions={{
          alignment: "fill",
          sortButtons: false,
          buttons: [
            {
              destructive: true,
              kind: "Secondary",
              type: "button",
              label: "Cancel",
              onClick: () => props.onClose(null)
            },
            {
              label: "Continue",
              loading: createBatchMeta.isLoading
            }
          ]
        }}
      >
        <WSList gap="XL">
          <WSText.ParagraphSm weight="book" color="gray600">
            A new batch will be created and ready for your formatted spreadsheet
            to be uploaded. Optionally, select an engagement for the batch, if
            applicable:
          </WSText.ParagraphSm>

          <WSForm.Field
            name="name"
            label="Batch name (optional)"
            component={WSInputText}
            componentProps={{
              placeholder: "Enter name for the import batch"
            }}
          />
          <WSForm.Field
            name="notes"
            label="Notes (optional)"
            component={WSInputTextarea}
            componentProps={{
              placeholder: "Provide notes for your reference",
              helperText: "Provide details about the batch for your reference"
            }}
          />

          <WSInfoBox>
            <WSText.ParagraphSm weight="book" color="gray600">
              Tip: adding a name and notes can be helpful when filtering by
              batches in the contractor list and help identify the purpose of an
              imported batch for your own reference.
            </WSText.ParagraphSm>
          </WSInfoBox>

          {engagementOptions.length > 0 && (
            <WSForm.Field
              name="engagementId"
              label="Engagement (optional)"
              component={WSSelect}
              componentProps={{
                internalSearch: true,
                placeholder: "Select an option",
                options: engagementOptions,
                isListLoading: queryEngagements.isLoading,
                tooltip: {
                  dark: true,
                  title: "Engagement (optional)",
                  children: ENGAGEMENT_TOOLTIP
                }
              }}
            />
          )}

          <WSInfoBox>
            <p>Reminders:</p>
            <ul>
              <li>An example template is available for download</li>
              <li>
                If you created the batch by accident or are not ready to upload
                the spreadsheet yet, you can delete the batch.
              </li>
            </ul>
          </WSInfoBox>

          <ErrorAlert context="BulkBatchCreate" error={createBatchMeta.error} />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalNewContractorBatch = () =>
  useWSModalCustom(ModalNewContractorBatch);
